import { Injectable, OnDestroy } from '@angular/core';
import { isNil } from 'lodash';
import { BehaviorSubject, forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap, take, tap } from 'rxjs/operators';

import { ConfigApiService, IConfigResult } from '../mobile-api';
import { SessionStorageService, STORAGE_KEYS } from '../storage/session-storage.service';

interface IMonitoredConfig {
	[key: string]: IConfigResult;
}

interface IMonitoredConfigMap {
	[key: string]: () => Observable<IConfigResult>;
}

/**
 * Monitor config values in session storage and refresh them when removed
 *
 * @export
 * @class ConfigService
 */
@Injectable({
	providedIn: 'root'
})
export class ConfigService implements OnDestroy {
	private readonly subscription = new Subscription();
	private readonly monitoredConfigSource = new BehaviorSubject<IMonitoredConfig>(null);

	// Exposed observable (read-only).
	readonly softPull$ = this.monitoredConfigSource.asObservable();
	// list of storage keys to monitor
	private readonly configStorageKeys = [STORAGE_KEYS.PLAID_CONNECT_ADDRESS_SCREEN_ORDER_SWAP_ENABLED];
	private readonly configFuncMap = {
		[STORAGE_KEYS.PLAID_CONNECT_ADDRESS_SCREEN_ORDER_SWAP_ENABLED]: () =>
			this.configApiService.configPlaidConnectAddressScreenOrderSwapEnabled()
	};

	constructor(private configApiService: ConfigApiService, private sessionStorageService: SessionStorageService) {}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	/**
	 * Load config values from the API and monitor them in session storage.
	 *
	 * @memberof ConfigService
	 */
	public loadConfigs(): void {
		this.updateAllConfig(this.configStorageKeys, this.configFuncMap).subscribe({
			next: () => {
				this.monitorConfigs(this.configStorageKeys, this.configFuncMap);
			}
		});
	}

	private monitorConfigs(configKeys: string[], configFuncMap: IMonitoredConfigMap): void {
		//monitor sessionStorage filled from config api.
		configKeys.forEach((key) => {
			const storageKeySub = this.sessionStorageService
				.select(key)
				.pipe(
					switchMap((value) => {
						return isNil(value) ? this.retrieveConfigValue(key, configFuncMap[key]) : of(null);
					}),
					finalize(() => {
						const config = this.monitoredConfigSource.getValue();
						this.monitoredConfigSource.next({ ...config, [key]: undefined });
					})
				)
				.subscribe();
			this.subscription.add(storageKeySub);
		});
	}

	private retrieveConfigValue(key: string, func: () => Observable<IConfigResult>): Observable<IConfigResult> {
		return func().pipe(
			take(1),
			tap((result: IConfigResult) => this.setConfig(key, result)),
			catchError(() => {
				this.setConfig(key, {
					value: false
				});
				return of(null);
			})
		);
	}

	private setConfig(key: string, result: IConfigResult): void {
		const config = this.monitoredConfigSource.getValue();
		if (config) {
			this.monitoredConfigSource.next({ ...config, [key]: result });
		} else {
			this.monitoredConfigSource.next({ [key]: result });
		}
		this.sessionStorageService.set(key, result.value);
	}

	private updateAllConfig(configKeys: string[], configFuncMap: IMonitoredConfigMap): Observable<void> {
		return forkJoin(configKeys.map((key) => this.retrieveConfigValue(key, configFuncMap[key]))).pipe(map(() => void 0));
	}
}
